<template>
  <!-- 手环维护弹窗 -->
  <a-modal
    :title="`手环维护 - ${wristbandName || ''}`"
    :visible="visible"
    @cancel="handleClose"
    :width="1200"
    :footer="null"
  >
    <!-- 条件搜索 -->
    <div class="table-page-search-wrapper" style="margin-bottom: 16px">
      <a-form layout="inline">
        <a-row :gutter="24">
          <a-col :md="8" :sm="24">
            <a-form-item label="岗位名称">
              <a-input
                v-model:value="queryParam.name"
                placeholder="请输入"
                allow-clear
                class="no-border-radius-input"
              />
            </a-form-item>
          </a-col>
          <a-col :md="8" :sm="24">
            <a-form-item label="故障现象">
              <a-input
                v-model:value="queryParam.malfunctionSymptoms"
                placeholder="请输入"
                allow-clear
                class="no-border-radius-input"
              />
            </a-form-item>
          </a-col>
          <!-- 故障原因筛选（默认隐藏） -->
          <a-col :md="8" :sm="24" v-show="expandSearch">
            <a-form-item label="故障原因">
              <a-input
                v-model:value="queryParam.causeOfFailure"
                placeholder="请输入"
                allow-clear
                class="no-border-radius-input"
              />
            </a-form-item>
          </a-col>
          <!-- 维修费用筛选（默认隐藏） -->
          <a-col :md="8" :sm="24" v-show="expandSearch">
            <a-form-item label="维修费用">
              <a-input-number
                v-model:value="queryParam.price"
                placeholder="请输入"
                :min="0"
                :precision="2"
                style="width: 100%"
                allow-clear
                class="no-border-radius-input-number"
              />
            </a-form-item>
          </a-col>
          <!-- 维保状态筛选（默认隐藏） -->
          <a-col :md="8" :sm="24" v-show="expandSearch">
            <a-form-item label="维保状态">
              <a-select
                v-model:value="queryParam.maintenanceStatus"
                placeholder="请选择"
                allow-clear
                style="width: 100%"
                class="no-border-radius-select"
              >
                <a-select-option
                  v-for="(d, index) in dict.type['maintenance_status']"
                  :key="index"
                  :value="d.value"
                >
                  {{ d.label }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <!-- 时间筛选（默认隐藏） -->
          <a-col :md="8" :sm="24" v-show="expandSearch">
            <a-form-item label="时间">
              <a-date-picker
                v-model:value="queryParam.operationTime"
                style="width: 100%"
                format="YYYY-MM-DD"
                valueFormat="YYYY-MM-DD"
                placeholder="请选择"
                allow-clear
                class="no-border-radius-picker"
              />
            </a-form-item>
          </a-col>
          <!-- 维修物流信息筛选（默认隐藏） -->
          <a-col :md="8" :sm="24" v-show="expandSearch">
            <a-form-item label="维修物流信息">
              <a-input
                v-model:value="queryParam.logisticsInformation"
                placeholder="请输入"
                allow-clear
                class="no-border-radius-input"
              />
            </a-form-item>
          </a-col>
          <!-- 查询和重置按钮 -->
          <a-col :md="8" :sm="24">
            <a-form-item>
              <span class="table-page-search-submitButtons">
                <a-button type="primary" @click="handleQuery" class="search-button">
                  搜索
                </a-button>
                <a-button style="margin-left: 8px" @click="handleReset" class="no-border-button reset-button">
                  重置
                </a-button>
                <a-button style="margin-left: 8px" @click="toggleExpandSearch" class="no-border-button toggle-button">
                  {{ expandSearch ? '收起' : '展开' }}
                  <a-icon :type="expandSearch ? 'up' : 'down'" />
                </a-button>
              </span>
            </a-form-item>
          </a-col>
        </a-row>
      </a-form>
    </div>

    <!-- 操作按钮 -->
    <div class="table-operations">
      <a-button
        type="text"
        shape="circle"
        @click="handleRefresh"
        v-hasPermi="refreshPermission"
        class="refresh-icon-button"
      >
        <img src="@/assets/images/projectUser/refresh.png" alt="刷新" />
      </a-button>
      <a-button type="primary" @click="handleAdd" v-hasPermi="addPermission" class="add-button">
        <a-icon type="plus" />新增
      </a-button>
      <a-button
        @click="handleExport"
        :disabled="!list || list.length === 0 || pagination.total === 0"
        v-hasPermi="exportPermission"
        class="no-border-button export-button"
      >
        <a-icon type="download" />导出
      </a-button>
      <a-button
        :disabled="multiple"
        @click="handleDelete"
        v-hasPermi="removePermission"
        class="no-border-button"
      >
        <a-icon type="delete" />删除
      </a-button>
    </div>

    <!-- 表格 -->
    <div v-if="list && list.length" style="margin-top: 20px">
      <a-table
        :columns="columns"
        :data-source="list"
        :pagination="false"
        :loading="loading"
        :row-selection="{
          selectedRowKeys: selectedRowKeys,
          onChange: onSelectChange,
        }"
        :scroll="{ x: 'max-content' }"
        rowKey="id"
        class="optimized-table"
        @change="handleTableChange"
      >
        <!-- 使用 bodyCell 插槽自定义单元格显示 -->
        <template #bodyCell="{ column, record, index }">
          <!-- 序号列：根据分页计算序号 -->
          <template v-if="column.dataIndex === 'id'">
            <span>
              {{ (pagination.current - 1) * pagination.pageSize + index + 1 }}
            </span>
          </template>
          <!-- 岗位名称列：显示岗位名称，如果记录中没有则使用props传入的岗位名称 -->
          <template v-else-if="column.dataIndex === 'name'">
            <span>
              {{ record.name || wristbandName || '-' }}
            </span>
          </template>
          <!-- 维修费用列：格式化显示 -->
          <template v-else-if="column.dataIndex === 'price'">
            <span>
              {{ record.price ? `¥${record.price}` : '-' }}
            </span>
          </template>
          <!-- 时间列：格式化显示 -->
          <template v-else-if="column.dataIndex === 'operationTime'">
            <span>
              {{ parseTime(record.operationTime) }}
            </span>
          </template>
          <!-- 维保状态列：使用字典标签显示 -->
          <template v-else-if="column.dataIndex === 'maintenanceStatus'">
            <span>
              <dict-tag
                :options="dict.type['maintenance_status']"
                :value="record.maintenanceStatus"
              />
            </span>
          </template>
          <!-- 操作列：编辑、删除 -->
          <template v-else-if="column.dataIndex === 'action'">
            <div class="table-action-buttons">
              <a @click="handleEdit(record)" v-hasPermi="editPermission" class="edit-link">
                编辑
              </a>
              <span class="action-divider">|</span>
              <a @click="handleDelete(record)" v-hasPermi="removePermission" class="delete-link">
                删除
              </a>
            </div>
          </template>
        </template>
      </a-table>
      
      <!-- 分页组件 -->
      <div class="pagination-wrapper">
        <div class="pagination-left">
          <span class="pagination-info">
            显示第 {{ (pagination.current - 1) * pagination.pageSize + 1 }} 到第 {{ Math.min(pagination.current * pagination.pageSize, pagination.total) }} 条数据/共 {{ pagination.total }} 条
          </span>
          <a-select
            v-model:value="pagination.pageSize"
            style="width: 100px; margin-left: 8px"
            @change="onShowSizeChange"
            class="no-border-radius-select"
          >
            <a-select-option :value="10">10条/页</a-select-option>
            <a-select-option :value="20">20条/页</a-select-option>
            <a-select-option :value="50">50条/页</a-select-option>
            <a-select-option :value="100">100条/页</a-select-option>
          </a-select>
        </div>
        <div class="pagination-right">
          <a-pagination
            :current="pagination.current"
            :total="pagination.total"
            :page-size="pagination.pageSize"
            :show-total="false"
            @change="changeSize"
          />
          <span class="pagination-jump">
            到第
            <a-input-number
              v-model:value="jumpPage"
              :min="1"
              :max="Math.ceil(pagination.total / pagination.pageSize)"
              style="width: 60px; margin: 0 4px"
              @pressEnter="handleJumpPage"
              class="no-border-radius-input-number"
            />
            页
          </span>
        </div>
      </div>
    </div>
    <a-empty v-else description="暂无手环维护数据" style="padding: 60px 0" />

    <!-- 新增/编辑表单 -->
    <wristband-maintenance-create-form
      ref="createForm"
      :wristband-id="wristbandId"
      :wristband-name="wristbandName"
      @ok="getList"
    />
  </a-modal>
</template>

<script>
import { $on, $off, $once, $emit } from '../../../../utils/gogocodeTransfer'
/**
 * 手环维护管理弹窗组件
 *
 * 功能说明：
 * 1. 提供手环维护记录的查询、新增、编辑、删除、导出功能
 * 2. 支持按岗位名称、故障现象、维保状态、时间等条件筛选
 * 3. 支持批量删除和导出操作
 * 4. 通过弹窗形式展示，需要传入手环ID和名称
 *
 * @author zbtech
 * @date 2025-01-XX
 */
import {
  pageEquipmentMaintenance,
  delEquipmentMaintenance,
} from '@/api/system/equipmentMaintenance'
import { tableMixin } from '@/store/table-mixin'
import WristbandMaintenanceCreateForm from './WristbandMaintenanceCreateForm'

export default {
  name: 'WristbandMaintenance',
  // 混入表格通用方法（如下载文件等）
  mixins: [tableMixin],
  // 使用的字典类型：维保状态、维保类型
  dicts: ['maintenance_status', 'maintenance_type'],
  components: {
    WristbandMaintenanceCreateForm,
  },
  props: {
    // 弹窗是否显示
    visible: {
      type: Boolean,
      default: false,
    },
    // 手环ID，用于查询该手环的维护记录
    wristbandId: {
      type: String,
      default: null,
    },
    // 岗位名称，用于显示在弹窗标题和表格中
    wristbandName: {
      type: String,
      default: '',
    },
    // 权限前缀，用于动态生成权限标识
    permissionPrefix: {
      type: String,
      default: 'system:wristband:maintenance',
    },
  },
  data() {
    return {
      // 维护记录列表数据
      list: [],
      // 表格加载状态
      loading: false,
      // 表格选中的行key数组
      selectedRowKeys: [],
      // 表格选中的行数据数组
      selectedRows: [],
      // 筛选项展开/收起状态（默认收起，只显示前两个筛选项）
      expandSearch: false,
      // 分页跳转
      jumpPage: 1,
      // 查询参数对象（与表格列字段顺序保持一致）
      queryParam: {
        name: undefined, // 岗位名称
        malfunctionSymptoms: undefined, // 故障现象
        causeOfFailure: undefined, // 故障原因
        price: undefined, // 维修费用
        maintenanceStatus: undefined, // 维保状态
        operationTime: null, // 操作时间
        logisticsInformation: undefined, // 维修物流信息
      },
      // 分页配置
      pagination: {
        current: 1, // 当前页码
        pageSize: 10, // 每页条数
        total: 0, // 总条数
        showSizeChanger: true, // 显示每页条数选择器
        showQuickJumper: true, // 显示快速跳转
        showTotal: (total) => `共 ${total} 条`, // 显示总数
      },
      // 表格列配置
      columns: [
        {
          title: '序号',
          dataIndex: 'id',
          align: 'center',
          width: 80,
        },
        {
          title: '岗位名称',
          dataIndex: 'name',
          align: 'center',
          width: 150,
        },
        {
          title: '故障现象',
          dataIndex: 'malfunctionSymptoms',
          align: 'center',
          width: 200,
        },
        {
          title: '故障原因',
          dataIndex: 'causeOfFailure',
          align: 'center',
          width: 200,
        },
        {
          title: '维修费用',
          dataIndex: 'price',
          align: 'center',
          width: 120,
        },
        {
          title: '维保状态',
          dataIndex: 'maintenanceStatus',
          align: 'center',
          width: 120,
        },
        {
          title: '时间',
          dataIndex: 'operationTime',
          align: 'center',
          width: 180,
        },
        {
          title: '维修物流信息',
          dataIndex: 'logisticsInformation',
          align: 'center',
          width: 200,
        },
        {
          title: '操作',
          dataIndex: 'action',
          align: 'center',
          width: 150,
          fixed: 'right',
        },
      ],
    }
  },
  computed: {
    // 刷新权限标识
    refreshPermission() {
      return [`${this.permissionPrefix}:refresh`]
    },
    // 新增权限标识
    addPermission() {
      return [`${this.permissionPrefix}:add`]
    },
    // 编辑权限标识
    editPermission() {
      return [`${this.permissionPrefix}:edit`]
    },
    // 删除权限标识
    removePermission() {
      return [`${this.permissionPrefix}:remove`]
    },
    // 导出权限标识
    exportPermission() {
      return [`${this.permissionPrefix}:export`]
    },
    // 获取手环维护类型对应的字典值
    // 用于在查询时过滤出类型为"手环维护"的记录
    wristbandMaintenanceTypeValue() {
      const dict = this.dict.type['maintenance_type'] || []
      const wristbandType = dict.find((item) => item.label === '手环维护')
      return wristbandType ? wristbandType.value : undefined
    },
    // 判断是否没有选中任何行（用于禁用删除按钮）
    multiple() {
      return !this.selectedRowKeys || this.selectedRowKeys.length === 0
    },
  },
  watch: {
    // 监听弹窗显示状态，当弹窗打开且有手环ID时，初始化数据
    visible(val) {
      if (val && this.wristbandId) {
        this.init()
      }
    },
    // 监听手环ID变化，当ID变化且弹窗打开时，重新初始化数据
    wristbandId(val) {
      if (val && this.visible) {
        this.init()
      }
    },
  },
  methods: {
    /**
     * 初始化组件数据
     * 重置分页、列表、选中项和查询条件，然后加载数据
     */
    init() {
      this.pagination.current = 1
      this.pagination.total = 0
      this.list = []
      this.selectedRowKeys = []
      this.selectedRows = []
      this.queryParam = {
        name: undefined,
        malfunctionSymptoms: undefined,
        causeOfFailure: undefined,
        price: undefined,
        maintenanceStatus: undefined,
        operationTime: null,
        logisticsInformation: undefined,
      }
      this.getList()
    },
    /**
     * 获取维护记录列表
     * 根据手环ID、维护类型和查询条件分页查询维护记录
     */
    getList() {
      // 校验手环ID
      if (!this.wristbandId) {
        this.$message.warning('手环ID不能为空', 3)
        return
      }
      this.loading = true
      // 构建查询参数（与表格列字段顺序保持一致）
      const params = {
        maintenanceId: this.wristbandId, // 手环ID（设备ID）
        type: this.wristbandMaintenanceTypeValue, // 维护类型：手环维护
        name: this.queryParam.name, // 岗位名称
        malfunctionSymptoms: this.queryParam.malfunctionSymptoms, // 故障现象
        causeOfFailure: this.queryParam.causeOfFailure, // 故障原因
        price: this.queryParam.price, // 维修费用
        maintenanceStatus: this.queryParam.maintenanceStatus, // 维保状态
        operationTime: this.queryParam.operationTime, // 操作时间
        logisticsInformation: this.queryParam.logisticsInformation, // 维修物流信息
        pageNum: this.pagination.current, // 当前页码
        pageSize: this.pagination.pageSize, // 每页条数
      }
      // 清除空值参数，避免传递undefined、null或空字符串
      Object.keys(params).forEach((key) => {
        if (
          params[key] === undefined ||
          params[key] === null ||
          params[key] === ''
        ) {
          delete params[key]
        }
      })
      // 调用分页查询接口
      pageEquipmentMaintenance(params)
        .then((res) => {
          this.list = res.data.records || []
          this.pagination.total = res.data.total || 0
          this.jumpPage = this.pagination.current
        })
        .finally(() => {
          this.loading = false
        })
    },
    /**
     * 处理查询操作
     * 重置到第一页并重新加载数据
     */
    handleQuery() {
      this.pagination.current = 1
      this.getList()
    },
    /**
     * 处理重置操作
     * 清空所有查询条件，重置到第一页并重新加载数据
     */
    handleReset() {
      this.queryParam = {
        name: undefined,
        malfunctionSymptoms: undefined,
        causeOfFailure: undefined,
        price: undefined,
        maintenanceStatus: undefined,
        operationTime: null,
        logisticsInformation: undefined,
      }
      this.pagination.current = 1
      this.getList()
    },
    /**
     * 处理刷新操作
     * 重新加载当前页数据
     */
    handleRefresh() {
      this.getList()
    },
    /**
     * 切换筛选项展开/收起状态
     * 控制是否显示额外的筛选项（默认只显示前两个）
     */
    toggleExpandSearch() {
      this.expandSearch = !this.expandSearch
    },
    /**
     * 处理新增操作
     * 打开新增表单弹窗
     */
    handleAdd() {
      // 校验手环ID
      if (!this.wristbandId) {
        this.$message.warning('手环ID不能为空', 3)
        return
      }
      // 调用子组件的新增方法
      this.$refs.createForm.handleAdd(this.wristbandId, this.wristbandName)
    },
    /**
     * 处理编辑操作
     * @param {Object} record - 要编辑的记录对象
     */
    handleEdit(record) {
      // 调用子组件的更新方法
      this.$refs.createForm.handleUpdate(record)
    },
    /**
     * 处理删除操作
     * @param {Object} record - 可选，要删除的单条记录。如果不传则删除选中的多条记录
     */
    handleDelete(record) {
      // 确定要删除的ID数组：如果传入了record则删除单条，否则删除选中的多条
      const ids = record ? [record.id] : this.selectedRowKeys
      if (!ids || ids.length === 0) {
        this.$message.warning('请先选择需要删除的数据', 3)
        return
      }
      const that = this
      // 确认删除对话框
      this.$confirm({
        title: '确认删除所选中数据?',
        content: `当前选中 ${ids.length} 条数据`,
        onOk() {
          // 构建删除参数：将ID数组转换为对象数组
          const data = ids.map((id) => ({ id }))
          return delEquipmentMaintenance(data)
            .then(() => {
              that.$message.success('删除成功', 3)
              // 清空选中项
              that.onSelectChange([], [])
              // 重新加载列表
              that.getList()
            })
            .catch(() => {
              that.$message.error('删除失败', 3)
            })
        },
      })
    },
    /**
     * 处理导出操作
     * 支持导出选中数据或当前筛选条件下的全部数据
     */
    handleExport() {
      // 判断是否有选中的数据
      const hasSelection =
        this.selectedRowKeys && this.selectedRowKeys.length > 0
      const that = this
      // 确认导出对话框
      this.$confirm({
        title: '是否确认导出?',
        content: hasSelection
          ? '将导出所选数据'
          : '将导出当前筛选条件下的全部数据',
        onOk() {
          // 构建导出参数（与表格列字段顺序保持一致）
          const params = {
            maintenanceId: that.wristbandId, // 手环ID
            type: that.wristbandMaintenanceTypeValue, // 维护类型
            name: that.queryParam.name, // 岗位名称
            malfunctionSymptoms: that.queryParam.malfunctionSymptoms, // 故障现象
            causeOfFailure: that.queryParam.causeOfFailure, // 故障原因
            price: that.queryParam.price, // 维修费用
            maintenanceStatus: that.queryParam.maintenanceStatus, // 维保状态
            operationTime: that.queryParam.operationTime, // 操作时间
            logisticsInformation: that.queryParam.logisticsInformation, // 维修物流信息
          }
          // 如果有选中数据，则传递选中ID数组
          if (hasSelection) {
            params.maintenanceIds = that.selectedRowKeys
          }
          // 清除空值参数
          Object.keys(params).forEach((key) => {
            if (
              params[key] === undefined ||
              params[key] === null ||
              params[key] === ''
            ) {
              delete params[key]
            }
          })
          // 调用下载方法（来自tableMixin）
          // 文件名格式：手环维护_岗位名称_时间戳.xls
          that.download(
            'system/equipment-maintenance/export/wristband',
            params,
            `手环维护_${that.wristbandName || ''}_${new Date().getTime()}.xls`
          )
        },
      })
    },
    /**
     * 处理关闭弹窗操作
     * 向父组件发送close事件
     */
    handleClose() {
      $emit(this, 'close')
    },
    /**
     * 处理表格行选择变化
     * @param {Array} selectedRowKeys - 选中的行key数组
     * @param {Array} selectedRows - 选中的行数据数组
     */
    onSelectChange(selectedRowKeys, selectedRows) {
      this.selectedRowKeys = selectedRowKeys
      this.selectedRows = selectedRows
    },
    /**
     * 处理表格变化（分页、排序等）
     * @param {Object} pagination - 分页对象，包含current和pageSize
     */
    handleTableChange(pagination) {
      // 更新分页信息
      this.pagination.current = pagination.current
      this.pagination.pageSize = pagination.pageSize
      // 清空选中项（切换页码时清空选择）
      this.onSelectChange([], [])
      // 重新加载数据
      this.getList()
    },
    /**
     * 分页大小改变事件
     * @param {Number} pageSize - 每页显示数量
     */
    onShowSizeChange(pageSize) {
      if (this.loading) return
      this.pagination.pageSize = pageSize
      this.pagination.current = 1
      this.jumpPage = 1
      this.getList()
    },
    /**
     * 分页改变事件
     * @param {Number} current - 当前页码
     * @param {Number} pageSize - 每页显示数量
     */
    changeSize(current, pageSize) {
      if (this.loading) return
      this.pagination.current = current
      this.pagination.pageSize = pageSize
      this.jumpPage = current
      this.getList()
    },
    /**
     * 分页跳转
     */
    handleJumpPage() {
      if (this.jumpPage && this.jumpPage >= 1) {
        const maxPage = Math.ceil(this.pagination.total / this.pagination.pageSize)
        if (this.jumpPage > maxPage) {
          this.jumpPage = maxPage
        }
        this.pagination.current = this.jumpPage
        this.getList()
      }
    },
  },
  emits: ['close'],
}
</script>

<style scoped>
/* 条件搜索区域样式 */
.table-page-search-wrapper :deep(.ant-form-item-label) {
  width: 80px;
  min-width: 80px;
}

.table-page-search-wrapper :deep(.ant-form-item-label > label) {
  width: 80px;
  text-align: right;
}

/* 移除所有输入框圆角的通用样式 */
.no-border-radius-input :deep(.ant-input) {
  border-radius: 0 !important;
}

.no-border-radius-input :deep(.ant-input-affix-wrapper) {
  border-radius: 0 !important;
}

.no-border-radius-picker :deep(.ant-picker) {
  border-radius: 0 !important;
}

.no-border-radius-select :deep(.ant-select-selector) {
  border-radius: 0 !important;
}

.no-border-radius-input-number :deep(.ant-input-number) {
  border-radius: 0 !important;
}

.no-border-radius-input-number :deep(.ant-input-number-input) {
  border-radius: 0 !important;
}

/* 搜索区域输入框样式覆盖 - 优先级高于 no-border-radius-input */
/* 普通输入框样式 */
.table-page-search-wrapper :deep(.no-border-radius-input .ant-input),
.table-page-search-wrapper :deep(.ant-form-item-control-input .ant-input) {
  background: #FFFFFF !important;
  border-radius: 4px !important;
  border: 1px solid #C7C7C7 !important;
}

/* 带前缀/后缀的输入框外层样式 */
.table-page-search-wrapper :deep(.no-border-radius-input .ant-input-affix-wrapper),
.table-page-search-wrapper :deep(.ant-input-affix-wrapper) {
  background: #FFFFFF !important;
  border-radius: 4px !important;
  border: 1px solid #C7C7C7 !important;
}

/* 带前缀/后缀的输入框内部input去掉边框，避免嵌套 */
.table-page-search-wrapper :deep(.ant-input-affix-wrapper .ant-input) {
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
}

/* 日期选择器样式 */
.table-page-search-wrapper :deep(.no-border-radius-picker .ant-picker),
.table-page-search-wrapper :deep(.ant-picker) {
  background: #FFFFFF !important;
  border-radius: 4px !important;
  border: 1px solid #C7C7C7 !important;
}

/* 下拉选择框样式 */
.table-page-search-wrapper :deep(.no-border-radius-select .ant-select-selector),
.table-page-search-wrapper :deep(.ant-select-selector) {
  background: #FFFFFF !important;
  border-radius: 4px !important;
  border: 1px solid #C7C7C7 !important;
}

.table-page-search-wrapper :deep(.ant-input-number) {
  border-radius: 0 !important;
}

/* 搜索区域数字输入框样式 */
.table-page-search-wrapper :deep(.no-border-radius-input-number.ant-input-number),
.table-page-search-wrapper :deep(.no-border-radius-input-number) {
  border-radius: 4px !important;
  border: 1px solid #C7C7C7 !important;
}

/* 搜索按钮和新增按钮样式 */
.search-button,
.add-button {
  background-color: #165DFF !important;
  border-color: #165DFF !important;
  border-radius: 4px !important;
}

.search-button:hover,
.add-button:hover {
  background-color: #4080ff !important;
  border-color: #4080ff !important;
}

.search-button:focus,
.add-button:focus {
  background-color: #165DFF !important;
  border-color: #165DFF !important;
}

/* 其他按钮圆角设置 */
.table-page-search-wrapper :deep(.ant-btn) {
  border-radius: 4px;
}

/* 按钮区域样式 */
.table-page-search-submitButtons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.table-operations :deep(.ant-btn) {
  border-radius: 4px;
}

/* 全局按钮圆角设置 */
:deep(.ant-btn) {
  border-radius: 4px !important;
}

.table-action-buttons a {
  color: #1890ff;
  cursor: pointer;
  white-space: nowrap; /* 防止操作按钮换行 */
}

.table-action-buttons a:hover {
  color: #40a9ff;
}

/* 编辑按钮颜色 */
.table-action-buttons .edit-link {
  color: #165DFF !important;
}

.table-action-buttons .edit-link:hover {
  color: #4080ff !important;
}

/* 删除按钮样式 */
.table-action-buttons .delete-link {
  font-family: PingFang SC, PingFang SC !important;
  font-weight: 400 !important;
  font-size: 14px !important;
  color: #ff4d4f !important;
}

.table-action-buttons .delete-link:hover {
  color: #ff7875 !important;
}

.action-divider {
  margin: 0 8px;
  color: #d9d9d9;
  white-space: nowrap;
}

/* 去掉指定按钮的边框 */
.no-border-button {
  border: none !important;
}

.no-border-button:hover,
.no-border-button:focus {
  border: none !important;
}

/* 重置按钮样式 */
.reset-button {
  background-color: #F3F3F3 !important;
  color: #101010 !important;
}

.reset-button:hover,
.reset-button:focus {
  background-color: #F3F3F3 !important;
  color: #101010 !important;
}

/* 导出按钮样式 */
.export-button {
  background-color: #F3F3F3 !important;
  color: #101010 !important;
}

.export-button:hover,
.export-button:focus {
  background-color: #F3F3F3 !important;
  color: #101010 !important;
}

/* 展开/收起按钮文字和图标颜色 */
.toggle-button {
  color: #165DFF !important;
}

.toggle-button :deep(.anticon) {
  color: #165DFF !important;
}

.toggle-button:hover,
.toggle-button:focus {
  color: #165DFF !important;
}

.toggle-button:hover :deep(.anticon),
.toggle-button:focus :deep(.anticon) {
  color: #165DFF !important;
}

.table-page-search-wrapper {
  margin-bottom: 16px;
}

.table-operations {
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.refresh-icon-button {
  width: 32px;
  height: 32px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination-wrapper {
  margin-top: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.pagination-left {
  display: flex;
  align-items: center;
}

.pagination-info {
  color: rgba(0, 0, 0, 0.65);
}

.pagination-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.pagination-jump {
  display: flex;
  align-items: center;
  color: rgba(0, 0, 0, 0.65);
}

/* 分页组件样式 - 去掉圆角 */
.pagination-wrapper :deep(.ant-btn) {
  border-radius: 0 !important;
}

.pagination-wrapper :deep(.ant-pagination-item) {
  border-radius: 0 !important;
}

.pagination-wrapper :deep(.ant-pagination-prev),
.pagination-wrapper :deep(.ant-pagination-next),
.pagination-wrapper :deep(.ant-pagination-prev .ant-pagination-item-link),
.pagination-wrapper :deep(.ant-pagination-next .ant-pagination-item-link) {
  border-radius: 0 !important;
}

.pagination-wrapper :deep(.ant-pagination-prev button),
.pagination-wrapper :deep(.ant-pagination-next button) {
  border-radius: 0 !important;
}

.pagination-wrapper :deep(.ant-input-number) {
  border-radius: 0 !important;
}

.pagination-wrapper :deep(.ant-input-number-input) {
  border-radius: 0 !important;
}

/* 分页选中页数样式 */
.pagination-wrapper :deep(.ant-pagination-item-active) {
  background: #165DFF !important;
  border-color: #165DFF !important;
  border-radius: 4px !important;
}

.pagination-wrapper :deep(.ant-pagination-item-active a) {
  color: #FFFFFF !important;
}

.pagination-wrapper :deep(.ant-pagination-item-active:hover) {
  background: #165DFF !important;
  border-color: #165DFF !important;
}

.pagination-wrapper :deep(.ant-pagination-item-active:hover a) {
  color: #FFFFFF !important;
}

/* 优化表格样式 */
.optimized-table :deep(.ant-table-thead > tr > th) {
  white-space: nowrap; /* 表头不换行 */
  overflow: hidden;
  text-overflow: ellipsis;
  background-color: #fafafa;
  font-family: PingFang SC, PingFang SC;
  font-weight: bold;
  font-size: 14px;
  color: #333333;
}

.optimized-table :deep(.ant-table-tbody > tr > td) {
  white-space: nowrap; /* 表格内容不换行 */
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 12px 8px; /* 增加内边距 */
  font-family: PingFang SC, PingFang SC !important;
  font-weight: 400 !important;
  font-size: 14px !important;
  color: #333333 !important;
}

/* 操作列固定右侧样式 */
.optimized-table :deep(.ant-table-tbody > tr > td:last-child) {
  position: sticky;
  right: 0;
  background: white;
  z-index: 10;
  box-shadow: -2px 0 5px rgba(0, 0, 0, 0.05);
}

/* 选中行的操作列样式 */
.optimized-table :deep(.ant-table-tbody > tr.ant-table-row-selected > td:last-child) {
  position: sticky;
  right: 0;
  background: white !important;
  z-index: 10;
  box-shadow: -2px 0 5px rgba(0, 0, 0, 0.05);
}

.optimized-table :deep(.ant-table-tbody > tr.ant-table-row-selected:hover > td:last-child) {
  position: sticky;
  right: 0;
  background: #f5f5f5 !important;
  z-index: 10;
  box-shadow: -2px 0 5px rgba(0, 0, 0, 0.05);
}

.optimized-table :deep(.ant-table-thead > tr > th:last-child) {
  position: sticky;
  right: 0;
  background: #fafafa;
  z-index: 11;
  box-shadow: -2px 0 5px rgba(0, 0, 0, 0.05);
}

/* 行悬停效果 */
.optimized-table :deep(.ant-table-tbody > tr:hover > td) {
  background-color: #f5f5f5;
}

/* 去掉表格边框 */
:deep(.ant-table) {
  border: none !important;
}

:deep(.ant-table-container) {
  border: none !important;
}

:deep(.ant-table-thead > tr > th) {
  border-bottom: 1px solid #f0f0f0 !important;
  border-right: none !important;
}

:deep(.ant-table-thead > tr > th:first-child) {
  border-left: none !important;
}

:deep(.ant-table-tbody > tr > td) {
  border-bottom: 1px solid #f0f0f0 !important;
  border-right: none !important;
}

:deep(.ant-table-tbody > tr > td:first-child) {
  border-left: none !important;
}

:deep(.ant-table-tbody > tr:last-child > td) {
  border-bottom: none !important;
}

/* 表格滚动条样式优化 */
:deep(.ant-table-body) {
  overflow-x: auto !important;
  scrollbar-width: thin;
  scrollbar-color: #d9d9d9 #f5f5f5;
}

:deep(.ant-table-body::-webkit-scrollbar) {
  height: 8px;
}

:deep(.ant-table-body::-webkit-scrollbar-track) {
  background: #f5f5f5;
  border-radius: 4px;
}

:deep(.ant-table-body::-webkit-scrollbar-thumb) {
  background: #d9d9d9;
  border-radius: 4px;
}

:deep(.ant-table-body::-webkit-scrollbar-thumb:hover) {
  background: #bfbfbf;
}

/* 序号列样式优化 */
:deep(.ant-table-tbody > tr > td:first-child) {
  font-family: PingFang SC, PingFang SC !important;
  font-weight: 400 !important;
  font-size: 14px !important;
  color: #333333 !important;
}

/* 选中的行样式 - 去掉背景色 */
:deep(.ant-table-tbody > tr.ant-table-row-selected > td) {
  background-color: transparent !important;
}

:deep(.ant-table-tbody > tr.ant-table-row-selected:hover > td) {
  background-color: transparent !important;
}

/* 表格复选框样式 */
.optimized-table :deep(.ant-checkbox-inner) {
  width: 16px !important;
  height: 16px !important;
  border-radius: 0 !important;
  border-color: #999999 !important;
}

.optimized-table :deep(.ant-checkbox-checked .ant-checkbox-inner) {
  background-color: #165DFF !important;
  border-color: #165DFF !important;
}

.optimized-table :deep(.ant-checkbox-checked .ant-checkbox-inner::after) {
  border-color: #FFFFFF !important;
}

.optimized-table :deep(.ant-checkbox:hover .ant-checkbox-inner) {
  border-color: #165DFF !important;
}

.optimized-table :deep(.ant-checkbox-input:focus + .ant-checkbox-inner) {
  border-color: #165DFF !important;
}

.optimized-table :deep(.ant-checkbox-checked:hover .ant-checkbox-inner) {
  background-color: #165DFF !important;
  border-color: #165DFF !important;
}
</style>
